import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, AlertCircle, Send, Phone, Clock, MapPin } from "lucide-react";
import Reveal from "@/components/base/Reveal";

// Web3Forms: envio direto para porta31condominios@gmail.com.
// O action e a access_key são públicos (é assim que o Web3Forms funciona) e
// tornam o formulário 100% independente — funciona em qualquer alojamento.
const WEB3FORMS_ACTION = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "f043303c-cd6c-40f3-931c-3303a9fbec09";

// Rota da página de agradecimento (usada no redirect do Web3Forms como
// fallback sem JavaScript e na navegação após envio assíncrono).
const THANKS_PATH = "/obrigado";
const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
const pathPrefix = basePath ? `/${basePath}` : "";
const thankYouUrl =
  typeof window !== "undefined" ? `${window.location.origin}${pathPrefix}${THANKS_PATH}` : "";

type Status = "idle" | "sending" | "success" | "error";

interface FieldErrors {
  nome?: string;
  email?: string;
  telemovel?: string;
  mensagem?: string;
}

const highlights = [
  { icon: Clock, title: "Resposta em 24 horas", text: "Entramos em contacto no prazo de um dia útil." },
  { icon: MapPin, title: "Reunião no seu condomínio", text: "Vamos até si." },
  { icon: Phone, title: "Sem compromisso", text: "Apresentamos uma proposta à medida da sua necessidade." },
];

export default function MeetingForm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string>("");

  const validate = (formData: FormData): FieldErrors => {
    const next: FieldErrors = {};
    const nome = String(formData.get("nome") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const telemovel = String(formData.get("telemovel") || "").trim();
    const mensagem = String(formData.get("mensagem") || "").trim();

    if (!nome) next.nome = "Por favor, indique o seu nome.";
    if (!email) next.email = "Por favor, indique o seu email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Introduza um email válido.";
    if (!telemovel) next.telemovel = "Por favor, indique o seu telemóvel.";
    else if (telemovel.replace(/\D/g, "").length < 9) next.telemovel = "Introduza um número de telemóvel válido.";
    if (!mensagem) next.mensagem = "Escreva uma breve mensagem sobre o seu condomínio.";

    return next;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Web3Forms honeypot: se estiver preenchido, é um bot. Não enviamos nada
    // e mostramos o mesmo feedback de sucesso para não o alertar.
    if (String(formData.get("botcheck") || "").trim()) {
      navigate(THANKS_PATH);
      return;
    }

    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setFormError("");
      return;
    }

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(WEB3FORMS_ACTION, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      const responseText = await response.text();
      let parsed: { success?: boolean; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.message || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.success === true && !isSpam;

      if (ok) {
        form.reset();
        navigate(THANKS_PATH);
      } else {
        setStatus("error");
        setFormError(
          serverMsg && serverMsg.length < 220
            ? serverMsg
            : "Não foi possível enviar o pedido. Tente novamente ou contacte-nos por telefone."
        );
      }
    } catch {
      setStatus("error");
      setFormError("Ocorreu um erro de ligação. Verifique a sua internet e tente novamente.");
    }
  };

  return (
    <section id="reunioes" className="relative w-full overflow-hidden bg-secondary-950 py-20 md:py-28">
      <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl" aria-hidden="true" />
      <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-accent-500/10 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-container px-4 md:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/30 bg-primary-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-300">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
              Marcação de Reuniões
            </span>
            <h2 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-background-50 md:text-4xl">
              Vamos falar sobre o seu condomínio?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-background-200 md:text-lg">
              Preencha o formulário e agendamos uma reunião sem compromisso. Explicamos como podemos simplificar a
              administração do seu edifício e responder a todas as suas dúvidas.
            </p>

            <div className="mt-9 space-y-4">
              {highlights.map((item) => (
                <div key={item.title} className="flex items-start gap-4 rounded-lg border border-background-50/10 bg-background-50/5 p-4">
                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-primary-500/15">
                    <item.icon className="h-5 w-5 text-primary-300" />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-semibold text-background-50">{item.title}</p>
                    <p className="mt-0.5 text-sm text-background-300">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <form
                  id="form-reuniao"
                  action={WEB3FORMS_ACTION}
                  method="POST"
                  onSubmit={handleSubmit}
                  noValidate
                  className="space-y-5"
                >
                  {/* Campos obrigatórios do Web3Forms */}
                  <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
                  <input type="hidden" name="subject" value="Novo pedido de reunião — Porta31 Condomínios" />
                  <input type="hidden" name="from_name" value="Site Porta31 Condomínios" />
                  <input type="hidden" name="redirect" value={thankYouUrl} />

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="nome" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        Nome <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="nome"
                        name="nome"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="O seu nome completo"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                      {errors.nome && <p className="mt-1.5 text-xs text-accent-700">{errors.nome}</p>}
                    </div>
                    <div>
                      <label htmlFor="telemovel" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        Telemóvel <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="telemovel"
                        name="telemovel"
                        type="tel"
                        required
                        autoComplete="tel"
                        placeholder="+351 900 000 000"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                      {errors.telemovel && <p className="mt-1.5 text-xs text-accent-700">{errors.telemovel}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Email <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="nome@exemplo.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                    {errors.email && <p className="mt-1.5 text-xs text-accent-700">{errors.email}</p>}
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="condominio" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        Condomínio <span className="text-foreground-400">(opcional)</span>
                      </label>
                      <input
                        id="condominio"
                        name="condominio"
                        type="text"
                        placeholder="Nome / morada do condomínio"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="data" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        Data preferencial
                      </label>
                      <input
                        id="data"
                        name="data"
                        type="date"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="mensagem" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      Mensagem <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      rows={4}
                      required
                      maxLength={500}
                      placeholder="Conte-nos um pouco sobre o seu condomínio e o que procura."
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-300 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                    {errors.mensagem && <p className="mt-1.5 text-xs text-accent-700">{errors.mensagem}</p>}
                  </div>

                  {/* Anti-spam (Web3Forms botcheck), escondido via CSS */}
                  <div className="form-guard-field" aria-hidden="true">
                    <label htmlFor="botcheck">Não preencher este campo</label>
                    <input
                      id="botcheck"
                      name="botcheck"
                      type="checkbox"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />
                  </div>

                  {status === "error" && formError && (
                    <div className="flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 p-3.5">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-accent-700" />
                      <p className="text-sm text-accent-800">{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-all duration-300 hover:scale-[1.01] hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 dark:text-foreground-950"
                  >
                    {status === "sending" ? (
                      <>
                        <CalendarCheck className="h-4 w-4 animate-pulse" />
                        A enviar...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Pedir Reunião
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-foreground-500">
                    Os seus dados são tratados com confidencialidade e usados apenas para responder ao seu pedido.
                  </p>
                </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}